// QuestionList.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  TextField,
  LinearProgress,
  Grid,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Container,
} from "@mui/material";
import WelcomeModal from "./WelcomeModal";
import QuestionProgress from "./QuestionProgress";
import "./Styles.css";
import "./UserInfoForm.css";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import DoneAllIcon from "@mui/icons-material/DoneAll";

const QuestionList = () => {
  const location = useLocation();
  const [questions, setQuestions] = useState(location.state?.questions || []);
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const navigate = useNavigate();
  const questionRefs = useRef([]);
  const [fetchError, setFetchError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { userId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchQuestions = () => {
    setLoading(true);
    setFetchError(false);

    axios
      .get(`${process.env.REACT_APP_API_URL}/questions`, {
        timeout: 15000, // 15-second timeout
      })
      .then((response) => {
        const fetchedQuestions = response.data.map((question) => ({
          ...question,
        }));
        setQuestions(fetchedQuestions);
        setLoading(false);
        // Show welcome modal after questions are loaded
        setShowWelcomeModal(true);
      })
      .catch((error) => {
        console.error("There was an error fetching the questions!", error);
        setLoading(false);
        setFetchError(true);
      });
  };

  useEffect(() => {
    // Clean up loading state when component unmounts
    if (localStorage.getItem("loadingQuestions") === "true") {
      localStorage.removeItem("loadingQuestions");
    }

    if (questions.length === 0) {
      fetchQuestions();
    } else {
      setLoading(false);
      // Show welcome modal if questions are already loaded
      setShowWelcomeModal(true);
    }
  }, []);

  useEffect(() => {
    if (questionRefs.current[currentPage * 5]) {
      questionRefs.current[currentPage * 5].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentPage]);

  const handleAnswerChange = (questionId, answerId) => {
    setAnswers({ ...answers, [questionId]: answerId });
    if (questionRefs.current[questionId]) {
      questionRefs.current[questionId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const validateAnswers = () => {
    const startIndex = currentPage * 5;
    const endIndex = startIndex + 5;
    for (let i = startIndex; i < endIndex; i++) {
      const question = questions[i];
      if (!answers[question.id]) {
        return `Please answer all questions on this page.`;
      }
    }
    return "";
  };

  const handleNext = () => {
    const error = validateAnswers();
    if (error) {
      setErrorMessage(error);
    } else {
      setErrorMessage("");
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSubmit = async () => {
    const error = validateAnswers();
    if (error) {
      setErrorMessage(error);
      return;
    }
    setErrorMessage("");
    setIsSubmitting(true);
    try {
      // Prepare answers array for backend
      const userAnswers = Object.entries(answers).map(
        ([questionId, answerId]) => ({
          userId,
          questionId: parseInt(questionId, 10),
          answerId: parseInt(answerId, 10),
        })
      );
      await axios.post(
        `${process.env.REACT_APP_API_URL}/useranswers`,
        userAnswers
      );
      navigate(`/dashboard/${userId}`);
    } catch (err) {
      setErrorMessage("Failed to submit answers. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
  };

  const calculateProgress = () => {
    const totalQuestions = questions.length;
    const answeredQuestions = Object.keys(answers).length;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const renderQuestions = () => {
    const startIndex = currentPage * 5;
    const endIndex = startIndex + 5;

    return questions.slice(startIndex, endIndex).map((question, index) => {
      return (
        <Box
          key={question.id}
          ref={(el) => (questionRefs.current[startIndex + index] = el)}
          sx={{
            my: 3,
            p: 3,
            borderRadius: "16px",
            backgroundColor: "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
            },
            border: answers[question.id]
              ? "1px solid rgba(223,116,104,0.3)"
              : "1px solid transparent",
          }}
        >
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    backgroundColor: "black",
                    color: "white",
                    borderRadius: "50%",
                    width: { xs: "30px", md: "36px" },
                    height: { xs: "30px", md: "36px" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  {startIndex + index + 1}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Merriweather, serif",
                    fontWeight: 600,
                    fontSize: { xs: "16px", md: "18px" },
                    lineHeight: 1.4,
                  }}
                >
                  {question.text}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <RadioGroup
                aria-label={`question-${question.id}`}
                name={`question-${question.id}`}
                value={answers[question.id] || ""}
                onChange={(e) =>
                  handleAnswerChange(question.id, e.target.value)
                }
              >
                {question.answers.map((answer) => (
                  <FormControlLabel
                    key={answer.id}
                    value={answer.id}
                    control={
                      <Radio
                        sx={{
                          color: "#bdbdbd",
                          "& .MuiSvgIcon-root": {
                            fontSize: 24,
                          },
                          "&.Mui-checked": {
                            color: "#DF7468",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: 1.6,
                          color: "#333",
                        }}
                      >
                        {answer.text}
                      </Typography>
                    }
                    sx={{
                      mx: 0.5,
                      my: 0.75,
                      padding: "8px 12px",
                      borderRadius: "8px",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>
          </Grid>
        </Box>
      );
    });
  };

  return (
    <Box
      className="questions-page-container"
      sx={{
        backgroundColor: "#f9fafc",
        minHeight: "100vh",
        paddingBottom: 5,
      }}
    >
      <WelcomeModal open={showWelcomeModal} onClose={handleCloseWelcomeModal} />

      {loading ? (
        <Container
          maxWidth="md"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
            textAlign: "center",
            gap: 4,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h5" sx={{ fontFamily: "Poppins, sans-serif" }}>
            Loading your assessment questions...
          </Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: "600px", fontFamily: "Poppins, sans-serif" }}
          >
            Please wait while we prepare your career wellness assessment.
          </Typography>
        </Container>
      ) : (
        <>
          <Box
            sx={{
              marginLeft: "10%",
              marginRight: "10%",
              marginBottom: "10px",
              marginTop: "20px",
            }}
          >
            <AppBar
              position="static"
              className="menu-bar"
              elevation={0}
              sx={{ borderRadius: 20, marginBottom: 2 }}
            >
              <Toolbar variant="regular">
                <IconButton edge="start" color="inherit" aria-label="menu">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "Merriweather, serif",
                        fontWeight: 700,
                        fontSize: { xs: "1.8rem", md: "32px" },
                        lineHeight: 1.1,
                        color: "#000000",
                      }}
                    >
                      Coach Carol
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 400,
                        fontSize: "12px",
                        color: "#000000",
                      }}
                    >
                      Powered by Career Agility
                    </Typography>
                  </Box>
                </IconButton>
              </Toolbar>
            </AppBar>

            <QuestionProgress
              currentPage={currentPage}
              totalPages={Math.ceil(questions.length / 5)}
              totalQuestions={questions.length}
              answeredCount={Object.keys(answers).length}
            />
          </Box>

          <Box
            sx={{
              marginLeft: "10%",
              marginRight: "10%",
            }}
          >
            {renderQuestions()}

            {errorMessage && (
              <Box
                sx={{
                  textAlign: "center",
                  mb: 2,
                  mx: "auto",
                  maxWidth: "500px",
                }}
              >
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    display: "inline-block",
                  }}
                >
                  {errorMessage}
                </Typography>
              </Box>
            )}

            <Box className="navigation-buttons">
              {currentPage !== 0 && (
                <Button
                  variant="contained"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  startIcon={<KeyboardDoubleArrowLeftIcon />}
                  sx={{
                    bgcolor: "#f0f0f0",
                    color: "#333",
                    borderRadius: "30px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    px: "24px",
                    py: "12px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "#e0e0e0",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Previous
                </Button>
              )}
              {currentPage < Math.ceil(questions.length / 5) - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<KeyboardDoubleArrowRightIcon />}
                  sx={{
                    background:
                      "linear-gradient(90deg, #F590AB 0%, #F2A442 60%, #DF7468 100%)",
                    color: "#fff",
                    borderRadius: "30px",
                    boxShadow: "0 2px 8px rgba(223,116,104,0.3)",
                    px: "24px",
                    py: "12px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:hover": {
                      opacity: 0.9,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  endIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DoneAllIcon />
                    )
                  }
                  sx={{
                    background:
                      "linear-gradient(90deg, #F590AB 0%, #F2A442 60%, #DF7468 100%)",
                    color: "#fff",
                    borderRadius: "30px",
                    boxShadow: "0 2px 8px rgba(223,116,104,0.3)",
                    px: "24px",
                    py: "12px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:hover": {
                      opacity: 0.9,
                    },
                    transition: "all 0.3s ease",
                  }}
                  disabled={isSubmitting}
                >
                  Submit
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default QuestionList;
